import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getVesselIGGFuelConsumption } from "../../../../services/vesselMachineryService";
import { generateDateTicks } from "../../../../services/dataTicks";
import { SlidersHorizontal } from "lucide-react";

const FuelConsAnalysisChart = () => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const { selectedVoyage } = useSelector((state) => state.voyageFilter);

  const [fuelConsumptionData, setFuelConsumptionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFuels, setSelectedFuels] = useState([
    "HFO",
    "LFO",
    "MGO",
    "others",
  ]);

  // Define color codes for each fuel type
  const fuelColors = {
    HFO: "#d35400", // Orange for Heavy Fuel Oil
    LFO: "#1a5276", // Blue for Light Fuel Oil
    MGO: "#2ecc71", // Green for Marine Gas Oil
    others: "#9b59b6", // Purple for Others
  };

  // Fuel type labels
  const fuelLabels = {
    HFO: "Heavy Fuel Oil",
    LFO: "Light Fuel Oil",
    MGO: "Marine Gas Oil",
    others: "Others",
  };

  // Fetch fuel consumption data from API
  useEffect(() => {
    const fetchFuelConsumptionData = async () => {
      if (!currentVessel?._id) {
        setFuelConsumptionData(null);
        return;
      }

      // Either voyage OR date range must be provided
      if (!selectedVoyage && (!startDate || !endDate)) {
        setFuelConsumptionData(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await getVesselIGGFuelConsumption(
          currentVessel._id,
          selectedVoyage ? null : startDate, // Pass null for dates if voyage selected
          selectedVoyage ? null : endDate,
          selectedVoyage?._id || null // Pass voyageId if selected
        );

        console.log("API Response Fuel Consumption IGG: ", response);

        if (response?.status === "success") {
          setFuelConsumptionData(response.data);
        } else {
          setError(
            response?.message || "Failed to fetch IGG fuel consumption data"
          );
          setFuelConsumptionData(null);
        }
      } catch (err) {
        console.error("Error fetching IGG fuel consumption data:", err);
        setError(err.message);
        setFuelConsumptionData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFuelConsumptionData();
  }, [currentVessel, startDate, endDate, selectedVoyage]); // Added selectedVoyage to dependencies

  // Transform API data to Highcharts format
  const transformDataForChart = () => {
    if (
      !fuelConsumptionData?.dailyData ||
      fuelConsumptionData.dailyData.length === 0
    ) {
      return {
        categories: [],
        series: [],
      };
    }

    const categories = fuelConsumptionData.dailyData.map((item) => item.date);

    const seriesData = {
      HFO: [],
      LFO: [],
      MGO: [],
      others: [],
    };

    fuelConsumptionData.dailyData.forEach((dayData, index) => {
      // Use the actual fullDate from the API response instead of calculating
      const fullDate = new Date(dayData.fullDate).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      seriesData.HFO.push({
        x: new Date(dayData.date).getTime(),
        y: dayData.HFO || 0,
        fullDate: fullDate,
        color: fuelColors.HFO,
      });

      seriesData.LFO.push({
        x: new Date(dayData.date).getTime(),
        y: dayData.LFO || 0,
        fullDate: fullDate,
        color: fuelColors.LFO,
      });

      seriesData.MGO.push({
        x: new Date(dayData.date).getTime(),
        y: dayData.MGO || 0,
        fullDate: fullDate,
        color: fuelColors.MGO,
      });

      seriesData.others.push({
        x: new Date(dayData.date).getTime(),
        y: dayData.others || 0,
        fullDate: fullDate,
        color: fuelColors.others,
      });
    });

    return {
      categories,
      series: selectedFuels.map((fuelType) => ({
        name: fuelLabels[fuelType],
        data: seriesData[fuelType],
        color: fuelColors[fuelType],
        unit: "MT/day",
      })),
    };
  };

  const chartData = transformDataForChart();

  // Debug: Check what data we're sending to Highcharts
  console.log("Chart Data:", chartData);
  console.log("Fuel Consumption Data:", fuelConsumptionData);

  const dates = fuelConsumptionData?.dailyData?.map((item) => item.date) || [];
  const tickPositions = generateDateTicks(dates);

  const options = {
    chart: {
      type: "line",
      height: "400",
      backgroundColor: "transparent",
      style: {
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
      },
    },
    title: null,
    xAxis: {
      type: "datetime",
      tickPositions: tickPositions,
      title: {
        text: "Days",
        style: {
          color: "#718096",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
      labels: {
        style: {
          color: "#4a5568",
          fontSize: "11px",
        },
        format: "{value:%b %e}",
        rotation: -45,
      },
    },
    yAxis: {
      title: {
        text: "Consumption (MT/Day)",
        style: {
          color: "#718096",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
      min: 0,
      startOnTick: true,
      minPadding: 0,
      maxPadding: 0.05,
      tickAmount: 6,
      labels: {
        style: {
          color: "#718096",
        },
      },
    },
    plotOptions: {
      area: {
        stacking: null,
        lineWidth: 2,
        marker: {
          enabled: true, // Enable markers to see zero values
          symbol: "circle",
          radius: 3,
          lineWidth: 1,
          lineColor: "#ffffff",
        },
        states: {
          hover: {
            enabled: true,
            lineWidth: 3,
          },
        },
      },
    },
    tooltip: {
      shared: true,
      backgroundColor: "rgba(255, 255, 255, 0.98)",
      borderColor: "#e2e8f0",
      borderRadius: 8,
      shadow: true,
      style: {
        fontSize: "12px",
      },
      headerFormat:
        '<span style="font-size: 13px; font-weight: 600; color: #1a365d">{point.key}</span><br/>',
      pointFormatter: function () {
        return `<span style="color:${this.color}">●</span> ${this.series.name
          }: <b>${this.y.toFixed(1)} MT/day</b><br/>`;
      },
    },
    series: chartData.series,
    credits: {
      enabled: false,
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: "#566573",
        fontSize: "11px",
        fontWeight: "500",
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              layout: "horizontal",
              align: "center",
              verticalAlign: "bottom",
            },
          },
        },
      ],
    },
  };

  const toggleFuelType = (fuelType) => {
    setSelectedFuels((prev) =>
      prev.includes(fuelType)
        ? prev.filter((f) => f !== fuelType)
        : [...prev, fuelType]
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a5276]"></div>
            <p className="mt-3 text-gray-600 font-medium">
              Loading IGG fuel consumption data...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-50 rounded-full mb-3">
              <svg
                className="w-6 h-6 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Unable to load data
            </h3>
            <p className="text-gray-600 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // No data selected state
  if (!fuelConsumptionData) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-3">
              <svg
                className="w-6 h-6 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              No data available
            </h3>
            <p className="text-gray-600 text-sm">
              {selectedVoyage
                ? "No voyage data selected or available"
                : "Please select a date range to view fuel consumption data"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Fuel Type Selector */}
      <div className="flex items-center gap-4 mb-4">
        <label className="text-base font-semibold text-gray-700 flex items-center gap-2">
          <SlidersHorizontal className="w-5 h-5 text-[#27ade3]" />
          Select by:
        </label>
        <div className="flex flex-wrap gap-3">
          {Object.entries(fuelLabels).map(([key, label]) => (
            <button
              key={key}
              onClick={() => toggleFuelType(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${selectedFuels.includes(key)
                  ? "text-white shadow-sm"
                  : "text-gray-600 bg-gray-100 hover:bg-gray-200"
                }`}
              style={{
                backgroundColor: selectedFuels.includes(key)
                  ? fuelColors[key]
                  : "",
                border: selectedFuels.includes(key)
                  ? `1px solid ${fuelColors[key]}`
                  : "1px solid #e2e8f0",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart - Always show if we have data */}
      {fuelConsumptionData &&
        fuelConsumptionData.dailyData &&
        fuelConsumptionData.dailyData.length > 0 ? (
        <HighchartsReact highcharts={Highcharts} options={options} />
      ) : (
        <div className="flex items-center justify-center h-64 border-2 border-dashed border-gray-300 rounded-lg">
          <div className="text-center">
            <svg
              className="w-12 h-12 text-gray-400 mx-auto mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <p className="text-gray-500 font-medium">No chart data available</p>
            <p className="text-gray-400 text-sm mt-1">
              {selectedVoyage
                ? "No fuel consumption data found for the selected voyage"
                : "No fuel consumption data found for the selected date range"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FuelConsAnalysisChart;
